import { cn, formatNumber } from '@worldcoo/ui/lib/utils';
import { Suspense } from 'react';
import { DonationsIcon } from './assets/donations-icon';
import { CollectedIcon } from './assets/collected-icon';
import { EmptyChar } from './empty-char';
import { useTranslation } from '@/lib/i18n';
import { useLingui } from '@lingui/react/macro';
import {
	usePortalCampaignQuery,
	usePortalMetadataQuery,
	usePortalCampaignsAggregatesQuery,
} from '@/lib/queries-portal';
import { PortalAnimateNumber } from './portal-animate-number';
import { useClient } from '@worldcoo/ui/hooks/use-client';
import { useDebounce } from '@worldcoo/ui/hooks/use-debounce';
import { isNotFound } from '@tanstack/react-router';

export function CampaignStatistics(props: { campaignId: string; textEffectDisabled?: boolean; variant?: 'sm' }) {
	const { t } = useLingui();
	const metadata = usePortalMetadataQuery();
	const aggregates = usePortalCampaignsAggregatesQuery();

	// Hide component when metadata or aggregates are unavailable
	if (isNotFound(metadata)) return null;
	if (!aggregates || !('aggregates' in aggregates)) {
		return null;
	}

	if (metadata.hideStatsOnActiveCampaigns && !aggregates.aggregates[props.campaignId]?.finished) {
		return null;
	}

	return (
		<>
			<div className={cn('bg-background flex gap-16', metadata.hideCollected && 'justify-center')}>
				<div className="flex flex-col font-medium">
					<DonationsIcon className={cn('text-primary mb-2', props.variant === 'sm' && 'size-6')} />
					<div
						className={cn('whitespace-nowrap text-base xl:text-2xl', props.variant === 'sm' && 'text-xl xl:text-xl')}
					>
						<Suspense fallback={<EmptyChar />}>
							<CampaignDonations campaignId={props.campaignId} textEffectDisabled={props.textEffectDisabled} />
						</Suspense>
					</div>
					<span
						className={cn(
							'text-primary text-nowrap text-xs font-semibold lg:text-base',
							props.variant === 'sm' && 'text-sm lg:text-sm',
						)}
					>
						{t`Donations`}
					</span>
				</div>

				{!metadata.hideCollected && (
					<div className="flex flex-col font-medium">
						<CollectedIcon className={cn('text-primary mb-2', props.variant === 'sm' && 'size-6')} />
						<div
							className={cn('whitespace-nowrap text-base xl:text-2xl', props.variant === 'sm' && 'text-xl xl:text-xl')}
						>
							<Suspense fallback={<EmptyChar />}>
								<CampaignCollected campaignId={props.campaignId} textEffectDisabled={props.textEffectDisabled} />
							</Suspense>
						</div>
						<div
							className={cn(
								'text-primary text-nowrap text-xs font-semibold lg:text-base',
								props.variant === 'sm' && 'text-sm lg:text-sm',
							)}
						>
							<Suspense fallback={<EmptyChar />}>
								<CampaignCollectedFromBudget campaignId={props.campaignId} />
							</Suspense>
						</div>
					</div>
				)}
			</div>
		</>
	);
}

function CampaignDonations(props: { campaignId: string; textEffectDisabled?: boolean }) {
	const { language } = useTranslation();
	const campaign = usePortalCampaignQuery(props.campaignId);
	const aggregates = usePortalCampaignsAggregatesQuery();
	const isClient = useClient();

	if (isNotFound(campaign)) return null;

	const number = useDebounce(
		!campaign.isPageLoaded && !props.textEffectDisabled && !isClient
			? 0
			: aggregates && 'aggregates' in aggregates
				? (aggregates.aggregates[props.campaignId]?.donors ?? 0)
				: 0,
		0,
	);

	return <PortalAnimateNumber testId="donations" locale={language} value={number} />;
}

function CampaignCollected(props: { campaignId: string; textEffectDisabled?: boolean }) {
	const { language } = useTranslation();
	const campaign = usePortalCampaignQuery(props.campaignId);
	const aggregates = usePortalCampaignsAggregatesQuery();
	const isClient = useClient();

	if (isNotFound(campaign)) return null;

	const number = useDebounce(
		!campaign.isPageLoaded && !props.textEffectDisabled && !isClient
			? 0
			: aggregates && 'aggregates' in aggregates
				? (aggregates.aggregates[props.campaignId]?.collected ?? 0)
				: 0,
		0,
	);

	return <PortalAnimateNumber testId="collected" locale={language} value={number} suffix="€" decimalDigits={2} />;
}

function CampaignCollectedFromBudget(props: { campaignId: string }) {
	const { t } = useLingui();
	const { language } = useTranslation();
	const aggregates = usePortalCampaignsAggregatesQuery();
	const funding =
		aggregates && 'aggregates' in aggregates ? aggregates.aggregates[props.campaignId]?.funding : undefined;

	if (!funding) return t`Collected`;
	const goal = formatNumber(language, funding, 0);
	return t`Goal of ${goal}` + ' €';
}
